<script setup lang="ts">
import { ref } from 'vue'

import type { IMenuItem } from '@/api/interfaces'

import LanguageSwitcher from '../LanguageSwitcher.vue'

interface Props {
  menuItemList?: IMenuItem[]
}

const props = defineProps<Props>()

const isOpen = ref(false)

const toggleMenu = () => (isOpen.value = !isOpen.value)
</script>

<template>
  <div class="burger-menu">
    <button :class="[{ 'is-open': isOpen }, 'burger-menu-button']" @click="toggleMenu">
      <span class="burger-menu-button-line" />
      <span class="burger-menu-button-line" />
      <span class="burger-menu-button-line" />
    </button>
    <nav v-if="isOpen" class="burger-menu-nav">
      <ul class="burger-menu-list">
        <li v-for="item in menuItemList" :key="item.link" class="burger-menu-list-item">
          <a
            :href="item.link"
            :target="item.target"
            :style="{ color: item.color, 'background-color': item.backgroundColor }"
            class="burger-menu-list-item-link"
          >
            {{ item.label }}
          </a>
        </li>
      </ul>
      <div class="burger-menu-buttons">
        <LanguageSwitcher />
        <GsButton
          href="https://www.app.getgreenspark.com/login"
          target="_blank"
          type="secondary"
          full-width
        >
          {{ $t('PublicHeader.buttons.login') }}
        </GsButton>
        <GsButton
          href="https://www.getgreenspark.com/account-type"
          target="_blank"
          type="success"
          full-width
        >
          {{ $t('PublicHeader.buttons.register') }}
        </GsButton>
      </div>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.burger-menu {
  position: relative;
  display: inline-block;
}

.burger-menu-buttons {
  display: flex;
  gap: 10px;
  flex-direction: column;
}

.burger-menu-button {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  background-color: transparent;
  border: none;
  cursor: pointer;

  @include media('md-and-up') {
    display: none;
  }

  &.is-open {
    .burger-menu-button-line {
      &:first-child {
        transform: rotateZ(45deg) translate(5px, 5px);
      }

      &:not(:first-child, :last-child) {
        display: none;
        opacity: 0;
      }

      &:last-child {
        transform: rotateZ(-45deg) translate(5px, -5px);
      }
    }
  }
}

.burger-menu-button-line {
  content: '';
  position: absolute;
  left: 4px;
  display: block;
  width: 24px;
  height: 2px;
  background-color: var(--ui-green);
  border-radius: 2px;
  transition: all 0.2s ease-in-out;

  &:first-child {
    top: 8px;
  }

  &:nth-child(2) {
    top: 50%;
    transform: translateY(-50%);
  }

  &:last-child {
    bottom: 8px;
  }
}

.burger-menu-nav {
  position: fixed;
  top: 64px;
  left: 0;
  width: 100%;
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 26px;
  background-color: var(--ui-white);
  overflow: hidden;
  border-radius: 0;

  .dark & {
    background-color: var(--darkest);
  }

  .btn {
    width: 100%;
  }
}

.burger-menu-list {
  list-style-type: none;
  padding: 0;
}

.burger-menu-list-item {
  border-bottom: 1px solid var(--ui-green-light);
  padding: 20px 0;
}

.burger-menu-list-item-link {
  display: block;
  text-decoration: none;
  font-size: 20px;
  font-weight: 600;
  color: inherit;
  text-transform: uppercase;
}
</style>
